﻿import { Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Skeleton,
  Switch,
  Tooltip,
} from 'antd'
import { SUB_DRA_MAPPINGS } from 'features/indicationTriage'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import {
  InspectionSummaryData,
  MainInfo,
  SummaryFormData,
  SummaryPageData,
} from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import { TYPE_TO_SHORT_NAME } from 'pages/reporting/shared/constants'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import {
  FormSection,
  FormSectionSubtitle,
  FormSectionTitle,
  SectionCard,
  StyledForm,
} from 'pages/reporting/shared/StyledComponents'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoInspectionType,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  GeckoSource,
} from 'types'

import { InspectionScope } from './subSections/summaryPageComponents/InspectionScope'
import { InspectionSummary } from './subSections/summaryPageComponents/InspectionSummary'

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

/**
 * PageWrapper - Container for the entire summary page with consistent spacing
 */
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

/**
 * HeaderBar - Compact top bar containing summary metrics
 */
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
  flex-wrap: wrap;
`

/**
 * MetricsRow - Horizontal layout for compact metrics display
 */
const MetricsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`

/**
 * MetricItem - Individual compact metric with label and value
 */
const MetricItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`

/**
 * MetricLabel - Small uppercase label for metrics
 */
const MetricLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  white-space: nowrap;
`

/**
 * MetricValue - Emphasized value display
 */
const MetricValue = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  white-space: nowrap;
`

/**
 * MetricDivider - Vertical separator between metrics
 */
const MetricDivider = styled.div`
  width: 1px;
  height: 1.25rem;
  background: ${({ theme }) => theme.forecast.cardDividerBackground};
`

/**
 * ContentCard - Main content card with improved styling
 */
const ContentCard = styled(SectionCard)`
  && {
    .ant-card-head {
      padding: 0 1.25rem;
      min-height: 3rem;
    }

    .ant-card-head-title {
      padding: 0.75rem 0;
      font-size: 0.875rem;
    }

    .ant-card-body {
      padding: 1.25rem;
    }
  }
`

/**
 * ScopeMethodLabel - Label for individual scope methods
 */
const ScopeMethodLabel = styled.span`
  font-weight: 500;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.activeText};
`

/**
 * StyledCollapse - Customized collapse component
 */
const StyledCollapse = styled(Collapse)`
  && {
    background: transparent;
    border: none;

    .ant-collapse-item {
      border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
      border-radius: 0.375rem !important;
      margin-bottom: 0.75rem;
      background: ${({ theme }) => theme.forecast.cardBackground};
      overflow: hidden;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .ant-collapse-header {
      padding: 0.75rem 1rem !important;
      font-size: 0.8125rem;
      font-weight: 500;
      color: ${({ theme }) => theme.forecast.activeText};
      background: ${({ theme }) => theme.forecast.cardGroupBackground};
    }

    .ant-collapse-content {
      border-top: 1px solid
        ${({ theme }) => theme.forecast.cardDividerBackground};
    }

    .ant-collapse-content-box {
      padding: 1rem;
    }
  }
`

/**
 * FormFieldRow - Responsive row for form fields
 */
const FormFieldRow = styled(Row)`
  && {
    margin-bottom: 0;
  }
`

/**
 * CompactFormSection - Tighter form section for grouped fields
 */
const CompactFormSection = styled(FormSection)`
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`

const isValidTemperatureUnit = (
  unit: string
): unit is 'Celsius' | 'Fahrenheit' | 'Ambient' => {
  return unit === 'Celsius' || unit === 'Fahrenheit' || unit === 'Ambient'
}

const surfaceTypes = [
  'O.D. Surface',
  'I.D. Surface',
  'Head',
  'Shell',
  'Channel',
] as const

const surfaceConditions = [
  'Painted/Coated',
  'Sand Blasted',
  'Power Buffed',
  'Rusted',
  'Poor',
  'Fair',
  'Clean',
] as const

const extractNominalThickness = (
  nominalsByInspection: ReadonlyMap<
    string,
    GeckoMeasurement | GeckoMeasurementRange
  >
): GeckoMeasurement | GeckoMeasurementRange | undefined => {
  const nominalValues = Array.from(nominalsByInspection.values())

  if (nominalValues.length === 0) {
    return undefined
  }

  const allMeasurements: GeckoMeasurement[] = []
  for (const nominal of nominalValues) {
    if (nominal instanceof GeckoMeasurement) {
      allMeasurements.push(nominal)
    } else if (nominal instanceof GeckoMeasurementRange) {
      allMeasurements.push(nominal.start)
      allMeasurements.push(nominal.end)
    }
  }

  if (allMeasurements.length === 0) {
    return undefined
  }

  const sortedMeasurements = allMeasurements.sort((a, b) => a.num - b.num)
  const min = sortedMeasurements[0]
  const max = sortedMeasurements[sortedMeasurements.length - 1]

  if (min.num === max.num) {
    return min
  }

  return GeckoMeasurementRange.CreateFromStartEnd(min, max)
}

const createMeasurementFieldProps = (displayUnit: GeckoMeasurementUnit) => ({
  normalize: (value: number | null | undefined) =>
    value === undefined || value === null
      ? undefined
      : GeckoMeasurement.CreateFromValue(value, displayUnit),
  getValueProps: (
    value:
      | GeckoMeasurement
      | {
          num?: number
          unit?: string
        }
      | number
      | undefined
  ) => {
    if (value instanceof GeckoMeasurement) {
      return { value: value.toNumber(displayUnit) }
    }

    if (typeof value === 'number') {
      return { value }
    }

    if (value && typeof value === 'object' && typeof value.num === 'number') {
      return { value: value.num }
    }

    return { value: undefined }
  },
})

const formatMeasurementDisplay = (
  measurement?:
    | GeckoMeasurement
    | {
        num?: number
        unit?: { id?: string } | string
      }
): string | undefined => {
  if (!measurement) {
    return undefined
  }

  if (measurement instanceof GeckoMeasurement) {
    return `${measurement.num} ${measurement.unit.id}`
  }

  if (typeof measurement === 'object' && typeof measurement.num === 'number') {
    const unit =
      typeof measurement.unit === 'string'
        ? measurement.unit
        : measurement.unit?.id
    return unit ? `${measurement.num} ${unit}` : String(measurement.num)
  }

  return undefined
}

const formatNominalThicknessDisplay = (
  nominal?: GeckoMeasurement | GeckoMeasurementRange
): string | undefined => {
  if (!nominal) {
    return undefined
  }

  if (nominal instanceof GeckoMeasurement) {
    return formatMeasurementDisplay(nominal)
  }

  const startDisplay = formatMeasurementDisplay(nominal.start)
  const endDisplay = formatMeasurementDisplay(nominal.end)

  if (!startDisplay) {
    return endDisplay
  }

  if (!endDisplay || startDisplay === endDisplay) {
    return startDisplay
  }

  return `${startDisplay} - ${endDisplay}`
}

const defaultInspectionScopes = [
  '100% of accessible piping sections',
  "the bottom 50% (3 o'clock to 9 o'clock position) of accessible piping sections",
  '100% of accessible shell area',
  '100% of accessible shell and head areas',
  '100% of accessible elbow sections',
]

const SUMMARY_CONTENT_BOTTOM_PADDING = 84
const SUMMARY_CONTENT_PADDING = 10
const SUMMARY_TEXT_ORPHANS = 4
const SUMMARY_TEXT_WIDOWS = 4

const RenderReact: React.FC = () => {
  const {
    formData,
    handleSummaryPageFinish,
    registerPageSaveHandler,
    handleMainInfoFinish,
    handleWeldScanDataFinish,
    scanCount,
    scanArea,
    totalUT,
    majorDamageRating,
    uniqueScanTypes,
    assetDiameter,
    assetLength,
    nominalsByInspection,
    isLoading,
    inspections,
  } = useRautReportOps()

  // Sum all scan counts from the map
  const totalScanCount = useMemo(() => {
    if (!scanCount) return undefined
    return Array.from(scanCount.values()).reduce((sum, count) => sum + count, 0)
  }, [scanCount])

  const pautScanCount = useMemo(() => {
    let count = 0
    inspections.forEach((insp) => {
      if (insp.data.inspection_type === GeckoSource.PhasedArray.id) {
        count += scanCount.get(insp.id) ?? 0
      }
    })
    return count
  }, [inspections, scanCount])

  const initialValues = useMemo(
    () => ({
      ...formData.mainInfo,
      ...formData.summaryPageData,
      weldScanData: {
        ...formData.weldScanData,
        totalScans: formData.weldScanData?.totalScans ?? pautScanCount,
      },
    }),
    [
      formData.mainInfo,
      formData.summaryPageData,
      formData.weldScanData,
      pautScanCount,
    ]
  )

  const [form] = Form.useForm<SummaryFormData>()
  const {
    measurementSystem,
    getGeometricUnit,
    getDistanceUnit,
    getTemperatureUnit,
    measurementPrecision,
  } = useUnitSystem()

  // Get units based on current measurement system
  const geometricUnit = getGeometricUnit()
  const distanceUnit = getDistanceUnit()
  const temperatureUnit = getTemperatureUnit()

  // Helper function to get unit display string
  const getUnitDisplayString = (unit: GeckoMeasurementUnit): string => {
    switch (unit) {
      case GeckoMeasurementUnit.Inches:
        return 'in'
      case GeckoMeasurementUnit.Millimeters:
        return 'mm'
      case GeckoMeasurementUnit.Celsius:
        return 'Celsius'
      case GeckoMeasurementUnit.Fahrenheit:
        return 'Fahrenheit'
      default:
        return unit.id
    }
  }

  const temperatureUnitStr = getUnitDisplayString(temperatureUnit)
  const distanceUnitStr = getUnitDisplayString(distanceUnit)
  const distanceMeasurementFieldProps = useMemo(
    () => createMeasurementFieldProps(distanceUnit),
    [distanceUnit]
  )

  const assetModelData = useMemo(
    () => ({
      diameter: assetDiameter?.convertTo(distanceUnit),
      length: assetLength?.convertTo(distanceUnit),
    }),
    [assetDiameter, assetLength, distanceUnit]
  )

  const nominalThicknessDisplay = useMemo(
    () =>
      formatNominalThicknessDisplay(
        extractNominalThickness(nominalsByInspection)
      ),
    [nominalsByInspection]
  )

  const surfaceTemperature = Form.useWatch('surfaceTemperature', form)
  const formTempUnit = Form.useWatch('temperatureUnit', form)
  const material = Form.useWatch('material', form)
  const scopesByScanType = Form.useWatch('scopesByScanType', form)

  // Track scope and summary via local state instead of Form.useWatch,
  // because these fields have no Form.Item in the parent form â€”
  // they're managed by child components and set via form.setFieldValue,
  // which Form.useWatch doesn't reliably detect without a bound Form.Item.
  const [, setInspectionScope] = useState<string | undefined>(
    formData.summaryPageData?.inspectionScope
  )
  const [, setInspectionSummary] = useState<InspectionSummaryData | undefined>(
    formData.summaryPageData?.inspectionSummary
  )

  const hasPAUT = useMemo(
    () => uniqueScanTypes.includes(GeckoSource.PhasedArray.id),
    [uniqueScanTypes]
  )

  // Sync form with initialValues when data changes
  // This handles the case where formData updates after isLoading becomes false
  useEffect(() => {
    if (isLoading) return

    const formattedInitialValues = { ...initialValues }

    // Set temperature unit if not already set
    if (formattedInitialValues.temperatureUnit === undefined) {
      formattedInitialValues.temperatureUnit = 'Ambient' as
        | 'Celsius'
        | 'Fahrenheit'
        | 'Ambient'
    }

    if (
      formattedInitialValues.diameter === undefined &&
      assetModelData.diameter !== undefined
    ) {
      formattedInitialValues.diameter = assetModelData.diameter
    }

    if (
      formattedInitialValues.length === undefined &&
      assetModelData.length !== undefined
    ) {
      formattedInitialValues.length = assetModelData.length
    }

    form.setFieldsValue(formattedInitialValues)

    // Sync local state for scope/summary with loaded data
    setInspectionScope(initialValues.inspectionScope)
    setInspectionSummary(initialValues.inspectionSummary)
  }, [
    assetModelData.diameter,
    assetModelData.length,
    form,
    initialValues,
    temperatureUnitStr,
    isLoading,
  ])

  // Update displayed units when measurement system changes
  useEffect(() => {
    if (!form) return

    // Force a form re-render to update displayed values with current unit system
    form.setFieldsValue({ ...form.getFieldsValue() })
  }, [measurementSystem, form])

  const handleInspectionScopeChange = useCallback(
    (scope: string) => {
      form.setFieldValue('inspectionScope', scope)
      setInspectionScope(scope)
    },
    [form]
  )

  const persistSummaryPageValues = useCallback(
    async (values: SummaryFormData) => {
      const formattedValues = { ...values }

      if (formattedValues.temperatureUnit === 'Ambient') {
        formattedValues.surfaceTemperature = undefined
      }

      const inspectionScopeValue = form.getFieldValue('inspectionScope')
      const inspectionSummaryValue = form.getFieldValue(
        'inspectionSummary'
      ) as InspectionSummaryData
      const weldScanData = form.getFieldValue('weldScanData')

      const completeValues: SummaryPageData & MainInfo = {
        ...initialValues,
        ...formattedValues,
        inspectionScope: inspectionScopeValue,
        inspectionSummary: inspectionSummaryValue,
      }

      await handleSummaryPageFinish(completeValues)
      await handleMainInfoFinish(completeValues)

      if (hasPAUT && weldScanData?.totalScans !== undefined) {
        await handleWeldScanDataFinish({
          totalScans: weldScanData.totalScans,
          scans: formData.weldScanData?.scans || [],
        })
      }
    },
    [
      form,
      formData.weldScanData?.scans,
      handleMainInfoFinish,
      handleSummaryPageFinish,
      handleWeldScanDataFinish,
      hasPAUT,
      initialValues,
    ]
  )

  const saveCurrentFormValues = useCallback(async () => {
    try {
      await persistSummaryPageValues(form.getFieldsValue())
    } catch (error) {
      console.error('Error saving summary form:', error)
      message.error('Failed to save summary page')
      throw error
    }
  }, [form, persistSummaryPageValues])

  useEffect(() => {
    registerPageSaveHandler('summary', async () => {
      await saveCurrentFormValues()
    })

    return () => {
      registerPageSaveHandler('summary', null)
    }
  }, [registerPageSaveHandler, saveCurrentFormValues])

  const handleInspectionScopeGenerate = saveCurrentFormValues

  const handleInspectionSummaryChange = useCallback(
    (summaryData: InspectionSummaryData) => {
      form.setFieldValue('inspectionSummary', summaryData)
      setInspectionSummary(summaryData)
    },
    [form]
  )

  const handleInspectionSummaryGenerate = saveCurrentFormValues

  const handleFormSubmit = useCallback(
    async (values: SummaryFormData) => {
      try {
        await persistSummaryPageValues(values)
      } catch (error) {
        console.error('Error submitting summary form:', error)
        message.error('Failed to save summary page')
      }
    },
    [persistSummaryPageValues]
  )

  // Format coverage percentage
  const coverageDisplay = useMemo(() => {
    if (scanArea === undefined || scanArea === null) return 'N/A'
    return `${(scanArea * 100).toFixed(1)}%`
  }, [scanArea])

  // Format damage rating
  const damageRatingDisplay = useMemo(() => {
    if (!majorDamageRating) return 'None'
    return `${SUB_DRA_MAPPINGS[majorDamageRating]}`
  }, [majorDamageRating])

  // Show loading skeleton until data is ready
  if (isLoading) {
    return (
      <PageWrapper>
        <HeaderBar>
          <Skeleton.Input active style={{ width: 400 }} />
        </HeaderBar>
        <ContentCard title='Basic Information'>
          <Skeleton active paragraph={{ rows: 2 }} />
        </ContentCard>
        <ContentCard title='Inspection Scope'>
          <Skeleton active paragraph={{ rows: 4 }} />
        </ContentCard>
        <ContentCard title='Inspection Summary'>
          <Skeleton active paragraph={{ rows: 4 }} />
        </ContentCard>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      {/* Header Bar with Metrics */}
      <HeaderBar>
        <MetricsRow>
          <MetricItem>
            <MetricLabel>Scans</MetricLabel>
            <MetricValue>{totalScanCount ?? 'N/A'}</MetricValue>
          </MetricItem>
          <MetricDivider />
          <MetricItem>
            <MetricLabel>Readings</MetricLabel>
            <MetricValue>
              {totalUT !== undefined && totalUT !== null
                ? totalUT.toLocaleString()
                : 'N/A'}
            </MetricValue>
          </MetricItem>
          <MetricDivider />
          <MetricItem>
            <MetricLabel>Coverage</MetricLabel>
            <MetricValue>{coverageDisplay}</MetricValue>
          </MetricItem>
          <MetricDivider />
          <Tooltip
            title={
              majorDamageRating
                ? `${SUB_DRA_MAPPINGS[majorDamageRating]} - ${majorDamageRating}`
                : 'No damage ratings assigned'
            }>
            <MetricItem>
              <MetricLabel>Damage Rating</MetricLabel>
              <MetricValue>{damageRatingDisplay}</MetricValue>
            </MetricItem>
          </Tooltip>
        </MetricsRow>
      </HeaderBar>

      <StyledForm<SummaryPageData & MainInfo>
        form={form}
        onFinish={handleFormSubmit}
        layout='vertical'
        initialValues={initialValues}
        disabled={formData.lockState?.isLocked}>
        {/* Basic Information Card */}
        <ContentCard title='Asset Information'>
          <FormFieldRow gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name='asset' label='Asset'>
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label='Nominal'>
                <Input value={nominalThicknessDisplay} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name='material' label='Material'>
                <Input placeholder='Enter material type' />
              </Form.Item>
            </Col>
          </FormFieldRow>

          <FormFieldRow gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label='Temperature'>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name='temperatureUnit' noStyle>
                      <Select
                        options={[
                          { label: 'Ambient', value: 'Ambient' },
                          {
                            label: temperatureUnitStr,
                            value: temperatureUnitStr,
                          },
                        ]}
                        onChange={(value) => {
                          if (isValidTemperatureUnit(value)) {
                            form.setFieldsValue({
                              temperatureUnit: value,
                              surfaceTemperature:
                                value === 'Ambient'
                                  ? undefined
                                  : form.getFieldValue('surfaceTemperature'),
                            })
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name='surfaceTemperature' noStyle>
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0}
                        step={0.1}
                        disabled={
                          formTempUnit === 'Ambient' ||
                          formData.lockState?.isLocked
                        }
                        placeholder={
                          formTempUnit === 'Ambient' ? 'Ambient' : 'Value'
                        }
                        formatter={(value: number | undefined) => {
                          if (formTempUnit === 'Ambient') {
                            return 'Ambient'
                          }
                          return String(value)
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name='diameter'
                label='Diameter'
                normalize={distanceMeasurementFieldProps.normalize}
                getValueProps={distanceMeasurementFieldProps.getValueProps}>
                <InputNumber
                  style={{ width: '100%' }}
                  addonAfter={distanceUnitStr}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name='length'
                label='Length'
                normalize={distanceMeasurementFieldProps.normalize}
                getValueProps={distanceMeasurementFieldProps.getValueProps}>
                <InputNumber
                  style={{ width: '100%' }}
                  addonAfter={distanceUnitStr}
                />
              </Form.Item>
            </Col>
          </FormFieldRow>

          <FormFieldRow gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name='inspectionSurface' label='Inspection Surface'>
                <Select
                  options={surfaceTypes.map((value) => ({
                    label: value,
                    value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name='surfaceCondition' label='Surface Condition'>
                <Select
                  options={surfaceConditions.map((value) => ({
                    label: value,
                    value,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name='coated' label='Coated' valuePropName='checked'>
                <Switch />
              </Form.Item>
            </Col>
          </FormFieldRow>
        </ContentCard>

        {hasPAUT && (
          <ContentCard title='Basic Information'>
            <FormFieldRow gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name={['weldScanData', 'totalScans']}
                  label='PAUT Weld Scans'
                  tooltip='Number of weld scans to include in the PAUT section'>
                  <InputNumber
                    min={0}
                    style={{ width: '100%' }}
                    placeholder='Enter count'
                  />
                </Form.Item>
              </Col>
            </FormFieldRow>
          </ContentCard>
        )}

        {/* Inspection Scope Card */}
        <ContentCard title='Inspection Scope'>
          {/* Scope Methods - Collapsible if multiple scan types */}
          {uniqueScanTypes.length > 0 && (
            <CompactFormSection>
              <FormSectionTitle>Scope by Method</FormSectionTitle>
              <FormSectionSubtitle>
                Define the inspection coverage for each scan method. These
                values are used when generating the scope text.
              </FormSectionSubtitle>

              {uniqueScanTypes.length === 1 ? (
                <Form.Item
                  name={['scopesByScanType', uniqueScanTypes[0]]}
                  label={
                    <ScopeMethodLabel>
                      {TYPE_TO_SHORT_NAME[
                        uniqueScanTypes[0] as GeckoInspectionType
                      ] || uniqueScanTypes[0].toUpperCase()}
                    </ScopeMethodLabel>
                  }
                  style={{ marginBottom: 0 }}>
                  <AutoComplete
                    options={defaultInspectionScopes.map((scope) => ({
                      value: scope,
                    }))}
                    placeholder={`Enter scope for ${TYPE_TO_SHORT_NAME[uniqueScanTypes[0]] || uniqueScanTypes[0]}`}
                    filterOption={(inputValue, option) =>
                      option?.value
                        .toUpperCase()
                        .indexOf(inputValue.toUpperCase()) !== -1
                    }>
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
                  </AutoComplete>
                </Form.Item>
              ) : (
                <StyledCollapse
                  defaultActiveKey={uniqueScanTypes}
                  items={uniqueScanTypes.map((scanType) => ({
                    key: scanType,
                    label:
                      TYPE_TO_SHORT_NAME[scanType as GeckoInspectionType] ||
                      scanType.toUpperCase(),
                    children: (
                      <Form.Item
                        name={['scopesByScanType', scanType]}
                        style={{ marginBottom: 0 }}>
                        <AutoComplete
                          options={defaultInspectionScopes.map((scope) => ({
                            value: scope,
                          }))}
                          placeholder={`Enter scope for ${TYPE_TO_SHORT_NAME[scanType] || scanType}`}
                          filterOption={(inputValue, option) =>
                            option?.value
                              .toUpperCase()
                              .indexOf(inputValue.toUpperCase()) !== -1
                          }>
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 4 }}
                          />
                        </AutoComplete>
                      </Form.Item>
                    ),
                  }))}
                />
              )}
            </CompactFormSection>
          )}

          {/* Generated Scope Text */}
          <FormSectionTitle>Generated Scope</FormSectionTitle>
          <FormSectionSubtitle>
            Review and edit the generated inspection scope text that will appear
            in the report.
          </FormSectionSubtitle>
          <Form.Item style={{ marginBottom: 0 }}>
            <InspectionScope
              geometricUnit={geometricUnit}
              surfaceTemperature={surfaceTemperature}
              temperatureUnit={formTempUnit}
              material={material}
              scopesByScanType={scopesByScanType}
              initialScope={initialValues?.inspectionScope}
              onChange={handleInspectionScopeChange}
              onGenerate={handleInspectionScopeGenerate}
            />
          </Form.Item>
        </ContentCard>

        {/* Inspection Summary Card */}
        <ContentCard title='Inspection Summary'>
          <Form.Item style={{ marginBottom: 0 }}>
            <InspectionSummary
              initialValues={initialValues.inspectionSummary}
              onChange={handleInspectionSummaryChange}
              onGenerate={handleInspectionSummaryGenerate}
              geometricUnit={geometricUnit}
              measurementPrecision={measurementPrecision}
              hasPAUT={hasPAUT}
            />
          </Form.Item>
        </ContentCard>
      </StyledForm>
    </PageWrapper>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRautReportOps()
  const data = React.useMemo(
    () => formData.summaryPageData,
    [formData.summaryPageData]
  )
  const { inspectionSummary, inspectionScope } = data

  return (
    <ReportPage
      id='summary'
      size='A4'
      tracker={{ sectionId: 'summary', onRender }}
      header={<PageHeader title='Inspection Summary' />}
      contentStyle={{ padding: SUMMARY_CONTENT_PADDING }}
      contentBottomPadding={SUMMARY_CONTENT_BOTTOM_PADDING}
      footer={
        <PageFooter
          jobNumber={formData.coverPageData.geckoJobId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
          prelim={
            !(
              formData.signoff?.reviewerSignoff &&
              formData.signoff.supervisorSignoff
            )
          }
        />
      }>
      {/* Inspection Scope Section */}
      <View style={PDFStyles.section}>
        <Text style={PDFStyles.sectionHeader}>Inspection Scope</Text>
        <Text style={PDFStyles.text}>{inspectionScope}</Text>
      </View>

      {/* Inspection Summary Section */}
      {inspectionSummary?.summary && (
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionHeader}>Inspection Summary</Text>
          <Text
            style={PDFStyles.text}
            orphans={SUMMARY_TEXT_ORPHANS}
            widows={SUMMARY_TEXT_WIDOWS}>
            {inspectionSummary.summary}
          </Text>
        </View>
      )}

      {inspectionSummary?.disclaimer && (
        <View style={PDFStyles.section}>
          <Text style={PDFStyles.sectionHeader}>Inspection Notes</Text>
          <Text
            style={PDFStyles.text}
            orphans={SUMMARY_TEXT_ORPHANS}
            widows={SUMMARY_TEXT_WIDOWS}>
            {inspectionSummary.disclaimer}
          </Text>
        </View>
      )}
    </ReportPage>
  )
}

export const Summary: ReportSection = {
  RenderPDF,
  RenderReact,
  required: true,
  id: 'summary',
  description: 'Inspection summary and findings',
  title: 'Inspection Summary',
  requiredFields: [],
}
